import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, ActivityIndicator, Alert } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useTranslation } from 'react-i18next';
import { getSavedPosts, removeSavedPost, SavedPost } from '../utils/saved';
import { Theme, useThemedStyles, useTheme } from '../theme';

interface Props {
  onBack: () => void;
  onSelectPost: (post: any) => void;
}

const SavedPosts: React.FC<Props> = ({ onBack, onSelectPost }) => {
  const insets = useSafeAreaInsets();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<SavedPost[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);

  const load = async () => {
    setLoading(true);
    const data = await getSavedPosts();
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleRemove = async (key: string) => {
    await removeSavedPost(key);
    load();
  };

  const renderItem = ({ item }: { item: SavedPost }) => (
    <TouchableOpacity style={styles.row} onPress={() => onSelectPost(item)}>
      <Image source={{ uri: item.author?.avatar || 'https://picsum.photos/200' }} style={styles.avatar} />
      <View style={styles.info}>
        <Text style={styles.name} numberOfLines={1}>{item.author?.displayName || item.author?.handle || t('savedPosts.post')}</Text>
        <Text style={styles.text} numberOfLines={2}>{item.text || item.record?.text || t('savedPosts.noText')}</Text>
        {item.localImages && item.localImages.length > 0 && (
          <View style={styles.imagePreviewRow}>
            {item.localImages.map((uri, i) => (
              <Image key={i} source={{ uri }} style={styles.miniImage} />
            ))}
          </View>
        )}
      </View>
      <TouchableOpacity style={styles.remove} onPress={() => handleRemove(item.uri || item.cid || '')}>
        <Ionicons name="trash-outline" size={20} color={theme.colors.textMuted} />
      </TouchableOpacity>
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <View style={[styles.container, { paddingBottom: insets.bottom }]}>
      <View style={[styles.header, { paddingTop: insets.top, height: 56 + insets.top }]}>
        <TouchableOpacity onPress={onBack} style={styles.backBtn}>
          <Ionicons name="chevron-back" size={24} color={theme.colors.text} />
        </TouchableOpacity>
        <Text style={styles.title}>{t('savedPosts.title')}</Text>
      </View>
      <FlatList
        data={items}
        keyExtractor={(item) => item.uri || item.cid || Math.random().toString()}
        renderItem={renderItem}
        refreshing={refreshing}
        onRefresh={() => { setRefreshing(true); load().then(() => setRefreshing(false)); }}
        ListEmptyComponent={
          <View style={styles.center}>
            <Text style={styles.empty}>{t('savedPosts.empty')}</Text>
          </View>
        }
      />
    </View>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: theme.colors.background },
  header: {
    height: 56,
    backgroundColor: theme.colors.card,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  backBtn: { padding: 4, marginRight: 8 },
  title: { color: theme.colors.text, fontSize: 18, fontWeight: '600' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.card,
  },
  avatar: { width: 40, height: 40, borderRadius: 16, backgroundColor: theme.colors.cardSecondary },
  info: { flex: 1, marginLeft: 12 },
  name: { fontSize: 14, fontWeight: '600', color: theme.colors.text },
  text: { fontSize: 12, color: theme.colors.textMuted, marginTop: 4 },
  imagePreviewRow: { flexDirection: 'row', marginTop: 8 },
  miniImage: { width: 40, height: 40, borderRadius: 4, marginRight: 4, backgroundColor: theme.colors.cardSecondary },
  remove: { padding: 6 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.colors.background },
  empty: { color: theme.colors.textMuted, fontSize: 14 },
});

export default SavedPosts;
